import { LockOutlined, PlusOutlined, UnlockOutlined } from '@ant-design/icons'
import { Dropdown } from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import {
  LibraTicketGroup,
  useCreateGroupForDashboard,
} from 'features/libra/ticket/libraTicketsApi'
import { useGeckoNotifications } from 'features/notifications'
import { useGeckoPermissions } from 'features/permissions'
import type { FC } from 'react'
import { useCallback, useState } from 'react'
import styled from 'styled-components'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

import { CreateGroupModal } from './CreateGroupModal'

type LibraDashboardGroupSelectProps = {
  groups: ReadonlyArray<LibraTicketGroup>
  selectedGroupId?: string
  onGroupSelect: (groupId: string | undefined) => void
  organizationId?: string
  onGroupCreated?: () => void
}

export const LibraDashboardGroupSelect: FC<LibraDashboardGroupSelectProps> = ({
  groups,
  selectedGroupId,
  onGroupSelect,
  organizationId,
  onGroupCreated,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const createGroup = useCreateGroupForDashboard()
  const { successNotification, errorNotification } = useGeckoNotifications()
  const { hasPermissions } = useGeckoPermissions()
  const { rolodexPatchAccessRestriction } = useRolodexEdit()

  const isInternalUser = hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER])

  const handleCreateGroup = useCallback(
    async (title: string, isInternal: boolean) => {
      if (!organizationId) {
        errorNotification('Organization ID is required to create a group')
        return
      }

      setIsCreating(true)
      try {
        await createGroup({
          title,
          organization: organizationId,
          isInternal,
        })
        successNotification('Group created successfully')
        setIsModalOpen(false)
        onGroupCreated?.()
      } catch (error) {
        console.error('Failed to create group:', error)
        errorNotification('Failed to create group')
      } finally {
        setIsCreating(false)
      }
    },
    [
      organizationId,
      createGroup,
      successNotification,
      errorNotification,
      onGroupCreated,
    ]
  )

  const handleToggleInternal = useCallback(
    async (group: LibraTicketGroup) => {
      const newRestriction =
        group.access_restriction === 'internal' ? null : 'internal'
      try {
        await rolodexPatchAccessRestriction({
          restriction: newRestriction,
          entry_ids: [group.id],
        })
        successNotification(
          newRestriction === 'internal'
            ? `"${group.title}" is now internal only`
            : `"${group.title}" is now visible to customers`
        )
        onGroupCreated?.()
      } catch (error) {
        console.error('Failed to update access restriction:', error)
        errorNotification('Failed to update group visibility')
      }
    },
    [
      rolodexPatchAccessRestriction,
      successNotification,
      errorNotification,
      onGroupCreated,
    ]
  )

  return (
    <>
      <Container>
        <ListContainer>
          {groups.map((group) => {
            const isInternal = group.access_restriction === 'internal'
            const button = (
              <GroupButton
                key={group.id}
                type='button'
                $isSelected={group.id === selectedGroupId}
                onClick={() => onGroupSelect(group.id)}>
                <GroupButtonContent>
                  <span>{group.title}</span>
                  {isInternal && (
                    <InternalBadge title='Internal only'>
                      <LockOutlined />
                    </InternalBadge>
                  )}
                </GroupButtonContent>
              </GroupButton>
            )

            if (!isInternalUser) return button

            return (
              <Dropdown
                key={group.id}
                trigger={['contextMenu']}
                menu={{
                  items: [
                    {
                      key: 'toggle-internal',
                      icon: isInternal ? <UnlockOutlined /> : <LockOutlined />,
                      label: isInternal
                        ? 'Make visible to customers'
                        : 'Make internal only',
                      onClick: () => handleToggleInternal(group),
                    },
                  ],
                }}>
                {button}
              </Dropdown>
            )
          })}
        </ListContainer>
        <AddButton
          type='button'
          onClick={() => setIsModalOpen(true)}
          aria-label='Create new group'>
          <PlusOutlined />
        </AddButton>
      </Container>
      <CreateGroupModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreateGroup={handleCreateGroup}
        isCreating={isCreating}
        showInternalOption={isInternalUser}
      />
    </>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const GroupButton = styled.button<{ $isSelected: boolean }>`
  border: 1px solid
    ${({ theme, $isSelected }) =>
      $isSelected ? theme.forecast.primary : theme.colors.gray[700]};
  background: ${({ theme, $isSelected }) =>
    $isSelected ? theme.forecast.background3 : 'transparent'};
  color: ${({ theme }) => theme.forecast.activeText};
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  text-align: left;
  cursor: pointer;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.forecast.background3};
  }
`

const GroupButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

const InternalBadge = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  opacity: 0.5;
  flex-shrink: 0;
`

const AddButton = styled.button`
  border: 1px dashed ${({ theme }) => theme.colors.gray[700]};
  background: transparent;
  color: ${({ theme }) => theme.forecast.defaultText};
  border-radius: 6px;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;

  &:hover {
    background: ${({ theme }) => theme.forecast.background3};
    border-color: ${({ theme }) => theme.forecast.primary};
    color: ${({ theme }) => theme.forecast.primary};
  }
`
